import React, { useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { ChartColumn, QrCode, Trash2 } from 'lucide-react';
import { SlideTransition } from '../../components/Transition/Transition';
import CoverQuizInsights from './CoverQuizInsights';
import { stampQuizApplied } from '../../store/clientQuiz/cache';
import QuizShareSection from '../Advisor/ClientV2/partials/QuizShareSection';
import {
  clearCoverQuiz,
  coverQuizPartsDone,
  coverResultOf,
  markCoverQuizDiscApplied,
  useQuizInvitations
} from './coverQuizStorage';
import type { CoverQuizPart } from './coverQuizStorage';
import type { DiscLetter } from './coverQuizData';
import type { PromptInput } from './coverQuizImport';
// Same dialog shell as 職涯測驗, so the two quizzes an adviser sends look alike.
import { pickCopy } from '../../utils/localeCopy';
import type { CopyLocale } from '../../utils/localeCopy';
import '../Advisor/ClientV2/partials/RecruitQuizDialog.scss';

/**
 * 保障小測驗 — the adviser's side: a QR code / link / WhatsApp invite to send,
 * and, once the client has finished, what the answers mean for the meeting.
 * Opened from the ClientV2 drawer's tile (after QuizChooserDialog picks a
 * part) and from Smart IDEA's 溝通方式 tab (the whole quiz).
 *
 * `part` 2 sends 第二部分 (理財目標) on its own; 1, the default, is the whole
 * quiz, which still offers 第二部分 at its end.
 *
 * The share section reads the server's own invitation state (§5.2, §5.5): a
 * live (unexpired) pending row for this part shows its QR/link; otherwise a
 * name field + one button sends a new one, worded by why (first send, resend
 * after expiry, or redo after completion).
 */

type PartCopy = Record<CoverQuizPart, { title: string; subtitle: string; invite: string; empty: string }>;

const PART_COPY: Record<CopyLocale, PartCopy> = {
  'zh-Hant-HK': {
    1: {
      title: '小測驗 · 第一部分',
      subtitle: '約 2 分鐘，推斷性格、顧慮、切入點和希望的服務方式',
      invite: '這個 2 分鐘的小測驗，可以認識你的理財想法，做完即時看到為你而設的測驗結果。我們見面時可以一起看看 😊',
      empty: '完成後這裏會自動顯示性格推斷、答錯的常識題（會面切入點）、想了解的保障、可能的顧慮和希望的服務方式。'
    },
    2: {
      title: '小測驗 · 第二部分：理財目標',
      subtitle: '約 1 分鐘，客戶揀一至兩個目標，得到目標參考報告',
      invite:
        '這個 1 分鐘的小工具，可以算算子女升學、置業或退休要準備多少、每月大約要存多少。做完我們可以一起詳細規劃 😊',
      empty: '完成後這裏會自動顯示客戶揀的理財目標、所需金額和每月儲蓄參考。'
    }
  },
  zh: {
    1: {
      title: '小测验 · 第一部分',
      subtitle: '约 2 分钟，推断性格、顾虑、切入点和希望的服务方式',
      invite: '这个 2 分钟的小测验，可以了解你的理财想法，做完即时看到为你而设的测验结果。我们见面时可以一起看看 😊',
      empty: '完成后这里会自动显示性格推断、答错的常识题（会面切入点）、想了解的保障、可能的顾虑和希望的服务方式。'
    },
    2: {
      title: '小测验 · 第二部分：理财目标',
      subtitle: '约 1 分钟，客户选一至两个目标，得到目标参考报告',
      invite:
        '这个 1 分钟的小工具，可以算算子女升学、置业或退休要准备多少、每月大约要存多少。做完我们可以一起详细规划 😊',
      empty: '完成后这里会自动显示客户选的理财目标、所需金额和每月储蓄参考。'
    }
  },
  en: {
    1: {
      title: 'Quick quiz · Part 1',
      subtitle: 'About 2 minutes: personality, concerns, conversation openers and preferred service style',
      invite:
        "This 2-minute quiz helps me understand how you think about money, and gives you a short personal report. Let's look at it together when we meet 😊",
      empty:
        'Once done, this shows the personality read, the knowledge questions they got wrong (good openers), the cover they want to learn about, likely concerns and their preferred service style.'
    },
    2: {
      title: 'Quick quiz · Part 2: financial goals',
      subtitle: 'About 1 minute: the client picks one or two goals and gets a reference report',
      invite:
        "This 1-minute tool works out how much you need for your children's education, a home or retirement, and roughly how much to save each month. Let's plan it in detail together afterwards 😊",
      empty: 'Once done, this shows the goals the client picked, the amount needed and a monthly savings reference.'
    }
  }
};

const SHARE_COPY = {
  'zh-Hant-HK': {
    heading: '📱 見面時掃碼即做',
    desc: '客戶用自己的手機做也可以，完成後會自動顯示在這裡。',
    copyLabel: '複製連結',
    copiedLabel: '已複製！',
    previewLabel: '預覽'
  },
  zh: {
    heading: '📱 见面时扫码即做',
    desc: '客户用自己的手机做也可以，完成后会自动显示在这里。',
    copyLabel: '复制链接',
    copiedLabel: '已复制！',
    previewLabel: '预览'
  },
  en: {
    heading: '📱 Scan and take it at the meeting',
    desc: 'The client can also take it on their own phone; the result shows up here automatically.',
    copyLabel: 'Copy link',
    copiedLabel: 'Copied!',
    previewLabel: 'Preview'
  }
};

const UI_COPY = {
  'zh-Hant-HK': {
    clearConfirm: '確定要清除這位客戶的測驗結果嗎？清除後可以重新測驗。',
    statusDone: '✅ 已完成',
    statusWaiting: '⏳ 待客戶作答',
    client: '客戶：',
    greeting: (name: string) => (name ? `${name}你好！` : '你好！'),
    analysis: '作答分析',
    emptyPart2: '客戶尚未完成第二部分',
    emptyPart1: '客戶尚未完成測驗',
    clear: '清除作答紀錄',
    close: '關閉'
  },
  zh: {
    clearConfirm: '确定要清除这位客户的测验结果吗？清除后可以重新测验。',
    statusDone: '✅ 已完成',
    statusWaiting: '⏳ 待客户作答',
    client: '客户：',
    greeting: (name: string) => (name ? `${name}你好！` : '你好！'),
    analysis: '作答分析',
    emptyPart2: '客户尚未完成第二部分',
    emptyPart1: '客户尚未完成测验',
    clear: '清除作答记录',
    close: '关闭'
  },
  en: {
    clearConfirm: "Clear this client's quiz result? They can take it again afterwards.",
    statusDone: '✅ Completed',
    statusWaiting: '⏳ Waiting for client',
    client: 'Client: ',
    greeting: (name: string) => (name ? `Hi ${name}! ` : 'Hi! '),
    analysis: 'Answer analysis',
    emptyPart2: "The client hasn't finished part 2 yet",
    emptyPart1: "The client hasn't taken the quiz yet",
    clear: 'Clear answers',
    close: 'Close'
  }
};

type IntlLike = {
  formatMessage: (descriptor: { id: string }, values?: Record<string, unknown>) => string;
  locale?: string;
};

export interface CoverQuizDialogProps {
  open: boolean;
  intl: IntlLike;
  onClose: () => void;
  clientId: string;
  /** The default display name sent with a new invitation — the factsheet's `name`, never the nickname. */
  clientName?: string;
  /** The DISC letter on file. */
  currentDisc?: string | null;
  /** Saves the quiz's DISC letter to the factsheet. Without it the button is hidden. */
  onApplyDisc?: (letter: DiscLetter) => void;
  /** 匯入客戶資料 — see CoverQuizInsights. Without `onImport` the list is hidden. */
  currentPromptInput?: PromptInput | null;
  onImport?: (next: PromptInput) => void;
  /** Which part the link starts; 1 (the whole quiz) by default. */
  part?: CoverQuizPart;
}

const CoverQuizDialog = (props: CoverQuizDialogProps) => {
  const {
    open,
    onClose,
    intl,
    clientId,
    clientName,
    currentDisc,
    onApplyDisc,
    currentPromptInput,
    onImport,
    part = 1
  } = props;
  const copy = pickCopy(PART_COPY, intl.locale)[part];
  const ui = pickCopy(UI_COPY, intl.locale);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    list,
    factsheetId,
    failed: listStale
  } = useQuizInvitations(clientId, 'cover', { pollMs: open ? 5000 : undefined, load: open });
  const result = useMemo(() => coverResultOf(clientId, list?.completed), [clientId, list]);
  const pending = list?.pending.find(q => q.part === part);
  const done = coverQuizPartsDone(result)[part === 2 ? 'two' : 'one'];

  const applyDisc = onApplyDisc
    ? (letter: DiscLetter) => {
        onApplyDisc(letter);
        markCoverQuizDiscApplied(clientId);
      }
    : undefined;

  const handleClear = () => {
    if (window.confirm(ui.clearConfirm)) clearCoverQuiz(clientId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      className="clientv2-recruit-quiz-modal cover-quiz-modal"
      data-testid="cover-quiz-dialog"
      slots={{ transition: SlideTransition }}
      slotProps={{ paper: { style: { borderRadius: isMobile ? 0 : 20, background: '#ffffff', overflow: 'hidden' } } }}
    >
      <DialogTitle className="rqd-title">
        <div className="rqd-title-row">
          <div className="rqd-title-left">
            <span className="rqd-title-icon">
              <QrCode size={22} />
            </span>
            <strong className="rqd-title-text">{copy.title}</strong>
          </div>
          <span className={`rqd-status-pill ${done ? 'is-done' : ''}`}>{done ? ui.statusDone : ui.statusWaiting}</span>
        </div>
        <p className="rqd-subtitle">
          {ui.client}
          <strong>{clientName}</strong> · {copy.subtitle}
        </p>
      </DialogTitle>

      <DialogContent dividers className="rqd-content">
        <QuizShareSection
          type="cover"
          part={part}
          factsheetId={factsheetId}
          pending={pending}
          done={done}
          defaultName={clientName}
          inviteText={(url, lang) =>
            `${pickCopy(UI_COPY, lang).greeting(clientName || '')}${pickCopy(PART_COPY, lang)[part].invite}\n${url}`
          }
          copy={pickCopy(SHARE_COPY, intl.locale)}
          testIdPrefix="cover-quiz"
          stale={listStale}
          locale={intl.locale}
        />

        {result && (done || part === 1) ? (
          <div className="rqd-result-card rqd-anim-item">
            <div className="rqd-result-head">
              <div className="rqd-result-head-left">
                <ChartColumn size={16} color="#475569" />
                <strong>{ui.analysis}</strong>
              </div>
            </div>
            <CoverQuizInsights
              result={result}
              intl={intl}
              clientId={clientId}
              currentDisc={currentDisc}
              onApplyDisc={applyDisc}
              currentPromptInput={currentPromptInput}
              onImport={
                onImport
                  ? next => {
                      onImport(next);
                      if (result?.id && factsheetId) {
                        void stampQuizApplied(factsheetId, 'cover', result.id).catch(() => undefined);
                      }
                    }
                  : undefined
              }
            />
          </div>
        ) : (
          <div className="rqd-empty-state rqd-anim-item">
            <p className="rqd-empty-title">{part === 2 ? ui.emptyPart2 : ui.emptyPart1}</p>
            <p className="rqd-empty-desc">{copy.empty}</p>
          </div>
        )}
      </DialogContent>

      <DialogActions className="rqd-actions">
        {result ? (
          <Button
            size="small"
            onClick={handleClear}
            startIcon={<Trash2 size={13} />}
            style={{ color: '#94a3b8', fontSize: 12, textTransform: 'none' }}
          >
            {ui.clear}
          </Button>
        ) : (
          <div />
        )}
        <Button onClick={onClose} color="primary" variant="outlined" style={{ borderRadius: 8 }}>
          {ui.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CoverQuizDialog;
